import { Button, SelectOption } from "@rio-cloud/rio-uikit";
import Switch from "@rio-cloud/rio-uikit/Switch";
import { useEffect, useRef, useState } from "react";
import { resolveTripSelection, TripDriverOption } from "@utils/rioTripOptions";
import type { RioTrip, RioTripsFilters } from "@types";
import InputSelect, { InactiveSelectOption } from "./select";
import { formatCurrency } from "@utils";
import moment from "moment";
import { useIntl } from "react-intl";
import { useT } from "@hooks";
import { deriveStatusFromDueDate, hasInvalidOverdueDueDate } from "@utils/transactionStatus";
import { getRecurrencePeriodErrorKey, toYearAnchoredDate } from "@utils/transactionRecurrence";
import { isMarketplaceTransaction } from "@utils/transactionSource";
import DueDateField from "@components/shared/DueDateField";
import RecurrenceFields, { buildRepeatEveryOptions, buildWeekDayOptions } from "@components/shared/RecurrenceFields";
import { useDriverSlots } from "@components/shared/useDriverSlots";
import { useDebouncedValue } from "@components/shared/useDebouncedValue";
import { useTripFiltersRequest } from "@components/shared/useTripFiltersRequest";
import { processAttachmentFileChange } from "@components/shared/attachmentFile";
import { buildEntityErrors } from "@components/shared/entityErrors";

type FormFields = {
  branch: string;
  segment: string;
  supplier: string;
};

type ModalKey = "filial" | "segmento" | "fornecedor";

export type FormState = {
  date: string;
  value: string;
  branch: string;
  segment: string;
  supplier: string;
  trip: string;
  vehicle: string;
  driver: string[];
  category: string;
  subcategory: string;
  observation: string;
  taxes: string;
  status?: string;
  dueDate?: string;
  isRecurring?: boolean;
  repeatEvery?: string;
  weekDays?: string[];
  recurrenceStart?: string;
  recurrenceEnd?: string;
  recurrenceInterval?: number;
  source?: string;
};

interface IReceiptFormProps {
  openFormModal: (toggle: boolean) => void;
  openNewOptionModal: (typeModal: string) => void;
  onSave: (form: FormState) => void;
  onSaveAndCreateNew: (form: FormState) => void;
  branchs: InactiveSelectOption[];
  segments: InactiveSelectOption[];
  suppliers: InactiveSelectOption[];
  drivers: TripDriverOption[] & SelectOption[];
  trips?: SelectOption[];
  tripsData?: RioTrip[];
  onTripFiltersChange?: (filters: RioTripsFilters) => void;
  categories: InactiveSelectOption[];
  vehicles: SelectOption[];
  subCategories: InactiveSelectOption[];
  form: FormState;
  fileName?: string;
  changeFileName: (fileName: string) => void;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  onCreateNewOption: (name: NameInputChange) => void;
  onCancel: () => void;
  handleFile?: (file: File) => void;
  isEdit?: boolean;
  onViewRecurrence?: () => void;
  selected?: FormState & { id?: string };
  handleClearFile: () => void;
  onInactiveSelect?: (item: InactiveSelectOption, fieldName: string) => void;
  onDownloadFile?: (transactionId: string) => void;
  transactionId?: string;
  hasNewAttachment?: boolean;
}

const MAX_DRIVERS = 3;

const TRIP_SEARCH_DELAY_MS = 400;

const RESET_BOTAO: React.CSSProperties = { background: "none", border: "none", padding: 0 };

const resolveMomentLocale = (locale: string) => {
  if (locale.startsWith("en")) return "en-gb";
  if (locale.startsWith("es")) return "es";
  return "pt-br";
};

const buildDriverSlots = (quantidade: number) =>
  Array.from({ length: quantidade }, (_, index) => ({ index, slotKey: `driver-slot-${index}` }));

export type NameInputChange =
  | "date"
  | "value"
  | "branch"
  | "segment"
  | "supplier"
  | "trip"
  | "vehicle"
  | "driver"
  | "category"
  | "subcategory"
  | "taxes"
  | "observation"
  | "hour"
  | "tollLocation"
  | "concessionaire"
  | "tagType"
  | "tagNumber"
  | "paymentMethod"
  | "status"
  | "repeatEvery"
  | "recurrenceStart"
  | "recurrenceEnd"
  | "maintenanceType";

const ReceiptForm = ({
  branchs,
  segments,
  suppliers,
  form,
  drivers,
  trips = [],
  tripsData = [],
  onTripFiltersChange,
  categories,
  subCategories,
  vehicles,
  isEdit,
  onViewRecurrence,
  fileName,
  onCancel,
  openFormModal,
  openNewOptionModal,
  onSave,
  onSaveAndCreateNew,
  setForm,
  onCreateNewOption,
  handleFile,
  handleClearFile,
  changeFileName,
  onInactiveSelect,
  selected,
  onDownloadFile,
  transactionId,
  hasNewAttachment,
}: IReceiptFormProps) => {
  const t = useT();
  const { locale } = useIntl();
  const momentLocale = resolveMomentLocale(locale);
  moment.locale(momentLocale);
  const isEditMode = isEdit ?? !!selected;
  const isStatusOnlyEdit = isEditMode && isMarketplaceTransaction(form.source);

  const STATUS_OPTIONS: SelectOption[] = [
    { id: "previsto", label: t("receipt.status.previsto") },
    { id: "realizado", label: t("receipt.status.realizado") },
    { id: "emAtraso", label: t("receipt.status.emAtraso") },
  ];

  const REPEAT_EVERY_OPTIONS = buildRepeatEveryOptions(t, "receipt");

  const WEEK_DAY_OPTIONS = buildWeekDayOptions(t, "receipt");

  const newOption = {
    id: "other",
    label: t("common.action.createNew"),
  };

  const DRIVER_LIMIT_TOOLTIP = t("receipt.form.driver.tooltip");

  const getDriverLabel = (index: number) => {
    if (index === 0) return t("receipt.form.driver.first", { total: MAX_DRIVERS });
    if (index === 1) return t("receipt.form.driver.second", { total: MAX_DRIVERS });
    return t("receipt.form.driver.last", { total: MAX_DRIVERS });
  };

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [errorFile, setErrorFile] = useState("");

  useEffect(() => {
    const map: Record<keyof FormFields, ModalKey> = {
      branch: "filial",
      segment: "segmento",
      supplier: "fornecedor",
    };

    const entries = Object.entries(map) as [keyof FormFields, ModalKey][];

    const found = entries.find(
      ([key]) => form[key] === "Outras..." || form[key] === "Outros..." || form[key] === "Novo Fornecedor..."
    );

    if (found) {
      const [, modalName] = found;
      openNewOptionModal(modalName);
      openFormModal(false);
    } else {
      openNewOptionModal("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const { driversQuantity, setDriversQuantity, inputDriverChange, inputClearDriver } = useDriverSlots(
    form?.driver,
    setForm,
    MAX_DRIVERS
  );

  const tripSelection = resolveTripSelection(
    tripsData?.find((trip) => trip.id === form.trip),
    drivers ?? [],
    vehicles ?? []
  );

  const isDriverLockedByTrip = !!tripSelection?.driverMatched;
  const isVehicleLockedByTrip = !!tripSelection?.vehicleMatched;

  const [tripSearch, setTripSearch] = useState("");
  const tripSearchAplicado = useDebouncedValue(tripSearch, TRIP_SEARCH_DELAY_MS);

  useTripFiltersRequest(
    {
      vehicleId: form.vehicle,
      search: tripSearchAplicado,
    },
    onTripFiltersChange
  );

  const handleTripChange = (value?: string) => {
    const selection = resolveTripSelection(
      tripsData?.find((trip) => trip.id === value),
      drivers ?? [],
      vehicles ?? []
    );

    if (selection?.driverMatched) setDriversQuantity(1);

    setForm((prev) => ({
      ...prev,
      trip: value ?? "",
      ...(selection?.driverMatched ? { driver: [selection.driverId] } : {}),
      ...(selection?.vehicleMatched ? { vehicle: selection.vehicleId } : {}),
    }));
  };

  useEffect(() => {
    (fileInputRef.current as HTMLInputElement).value = "";
  }, [fileName]);

  const isRecurrenceActive = !!form.isRecurring;

  const overdueDueDateError = hasInvalidOverdueDueDate(form.status, form.dueDate)
    ? t("receipt.form.validation.dueDate.overdue", { date: moment().format("DD/MM/YYYY") })
    : undefined;

  useEffect(() => {
    if (isEditMode) return;

    setForm((prev) => (prev.date ? prev : { ...prev, date: moment().format("YYYY-MM-DD") }));
  }, [isEditMode, form.date, setForm]);

  useEffect(() => {
    if (isEditMode) return;

    const canonical = deriveStatusFromDueDate(form.dueDate);
    if (!canonical) return;
    const derived = canonical === "vencido" ? "emAtraso" : canonical;
    setForm((prev) => (prev.status === derived ? prev : { ...prev, status: derived }));
  }, [isEditMode, form.dueDate, setForm]);

  useEffect(() => {
    if (form.repeatEvery !== "ano") return;
    setForm((prev) => {
      const anchor = prev.dueDate;
      const start = toYearAnchoredDate(prev.recurrenceStart, anchor) ?? prev.recurrenceStart;
      const end = toYearAnchoredDate(prev.recurrenceEnd, anchor) ?? prev.recurrenceEnd;
      if (start === prev.recurrenceStart && end === prev.recurrenceEnd) return prev;
      return { ...prev, recurrenceStart: start, recurrenceEnd: end };
    });
  }, [form.repeatEvery, form.dueDate, setForm]);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    processAttachmentFileChange(
      event,
      { tooLarge: t("receipt.form.file.tooLarge"), invalidType: t("receipt.form.file.invalidType") },
      { setError: setErrorFile, changeFileName, onFile: handleFile }
    );
  };

  const inputChange = (name: NameInputChange | keyof FormState, value?: string) => {
    if (name === "value" && value) {
      const formmated = formatCurrency(value, false);
      setForm((prev) => ({
        ...prev,
        [name]: formmated,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = () => {
    const newErrors = validate();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(form);
  };

  const handleSubmitNew = () => {
    const newErrors = validate();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSaveAndCreateNew(form);
  };

  const validate = (): Partial<Record<keyof FormState, string>> => {
    if (isStatusOnlyEdit) return overdueDueDateError ? { dueDate: overdueDueDateError } : {};

    const newErrors: Partial<Record<keyof FormState, string>> = {};

    if (!form.dueDate) newErrors.dueDate = t("receipt.form.validation.dueDate.required");

    if (!form.value) newErrors.value = t("receipt.form.validation.value.required");
    else if (Number(form.value) <= 0) newErrors.value = t("receipt.form.validation.value.positive");

    Object.assign(
      newErrors,
      buildEntityErrors(
        form,
        { branchs, segments, suppliers, categories, subCategories },
        {
          branchRequired: t("receipt.form.validation.branch.required"),
          branchInactive: t("receipt.form.validation.branch.inactive"),
          segmentInactive: t("receipt.form.validation.segment.inactive"),
          supplierInactive: t("receipt.form.validation.supplier.inactive"),
          categoryRequired: t("receipt.form.validation.category.required"),
          categoryInactive: t("receipt.form.validation.category.inactive"),
          subcategoryInactive: t("receipt.form.validation.subcategory.inactive"),
        }
      )
    );

    if (isRecurrenceActive) {
      const periodErrorKey = getRecurrencePeriodErrorKey(form.recurrenceStart || form.dueDate, form.recurrenceEnd);
      if (periodErrorKey) newErrors.recurrenceEnd = t(periodErrorKey);
    }

    if (overdueDueDateError) newErrors.dueDate = overdueDueDateError;

    return newErrors;
  };

  const fileClear = () => {
    changeFileName("");
    handleClearFile();
  };

  return (
    <>
      <div className="padding-x-20">
        <form>
          <fieldset>
            <div>
              {isEditMode && isRecurrenceActive && (
                <div className="margin-bottom-20">
                  <button
                    type="button"
                    className="btn btn-default text-color-darker border-color-light hover-bg-lighter hover-text-color-darker hover-border-color-light"
                    onClick={onViewRecurrence}
                  >
                    <span className="rioglyph rioglyph-calendar" aria-hidden="true" />
                    {t("receipt.form.viewRecurrence")}
                  </button>
                </div>
              )}

              <div className="display-flex gap-15 flex-wrap">
                <div className="flex-1-1 width-100pct-xs">
                  <InputSelect
                    disabled={
                      isStatusOnlyEdit || (isEditMode && !!categories.find((c) => c.id === form.category)?.inactive)
                    }
                    inputChange={inputChange}
                    label={t("receipt.form.label.category")}
                    name="category"
                    newOption={newOption}
                    onCreateNewOption={onCreateNewOption}
                    options={categories}
                    value={form.category}
                    errorMessage={errors.category}
                    onInactiveSelect={(item) => onInactiveSelect?.(item, "category")}
                    inactiveEntityLabel="categoria"
                  />
                </div>
                <div className="flex-1-1 width-100pct-xs">
                  <InputSelect
                    inputChange={inputChange}
                    label={t("receipt.form.label.status")}
                    name="status"
                    onCreateNewOption={() => {}}
                    options={STATUS_OPTIONS}
                    value={form.status ?? ""}
                    useClear
                  />
                </div>
              </div>
            </div>
          </fieldset>

          <fieldset disabled={isStatusOnlyEdit}>
            <div>
              <InputSelect
                disabled={
                  !form.category || (!!isEditMode && !!subCategories.find((s) => s.id === form.subcategory)?.inactive)
                }
                inputChange={inputChange}
                label={t("receipt.form.label.subcategory")}
                name="subcategory"
                newOption={newOption}
                onCreateNewOption={onCreateNewOption}
                options={subCategories}
                value={form.subcategory}
                errorMessage={errors.subcategory}
                onInactiveSelect={(item) => onInactiveSelect?.(item, "subcategory")}
                inactiveEntityLabel="subcategoria"
              />

              <div className="display-flex gap-15 flex-wrap">
                <DueDateField
                  className="flex-1-1 form-group width-100pct-xs"
                  label={t("receipt.form.label.dueDate")}
                  placeholder={t("common.placeholder.selectDate")}
                  momentLocale={momentLocale}
                  value={form.dueDate}
                  errorMessage={errors.dueDate ?? overdueDueDateError}
                  onChange={(value) => inputChange("dueDate", value)}
                />
                <div className="form-group display-flex flex-column">
                  <span className="margin-bottom-5" aria-hidden="true">
                    &nbsp;
                  </span>
                  <div className="display-flex flex-row flex-1-1 align-items-center gap-5">
                    <label className="margin-0 text-size-12 text-color-gray white-space-nowrap">
                      {t("receipt.form.label.recurring")}
                    </label>
                    <Switch
                      checked={!!form.isRecurring}
                      onChange={(checked) => setForm((prev) => ({ ...prev, isRecurring: checked }))}
                    />
                  </div>
                </div>
              </div>

              {form.isRecurring && (
                <RecurrenceFields
                  className="display-flex align-items-end gap-10 flex-wrap"
                  labels={{
                    repeatEvery: t("receipt.form.label.repeatEvery"),
                    weekDays: t("receipt.form.label.weekDays"),
                    recurrenceStartYear: t("receipt.form.label.recurrenceStartYear"),
                    recurrenceEndYear: t("receipt.form.label.recurrenceEndYear"),
                    recurrenceStart: t("receipt.form.label.recurrenceStart"),
                    recurrenceEnd: t("receipt.form.label.recurrenceEnd"),
                    datePlaceholder: t("common.placeholder.selectDate"),
                  }}
                  repeatEveryOptions={REPEAT_EVERY_OPTIONS}
                  weekDayOptions={WEEK_DAY_OPTIONS}
                  momentLocale={momentLocale}
                  repeatEvery={form.repeatEvery}
                  weekDays={form.weekDays}
                  recurrenceStart={form.recurrenceStart}
                  recurrenceEnd={form.recurrenceEnd}
                  recurrenceEndError={errors.recurrenceEnd}
                  dueDate={form.dueDate}
                  inputChange={inputChange}
                  onWeekDaysChange={(weekDays) => setForm((prev) => ({ ...prev, weekDays }))}
                />
              )}

              <div className={`form-group ${errors.value ? "has-feedback has-error" : ""}`}>
                <label htmlFor="inputValue">
                  <span>{t("receipt.form.label.value")}</span>
                </label>
                <div className="input-group">
                  <span className="input-group-addon text-normal">R$</span>
                  <input
                    id="inputValue"
                    type="text"
                    className="form-control"
                    value={form.value}
                    onChange={(e) => inputChange("value", e.target.value)}
                    name="value"
                  />
                </div>
                {errors.value ? <span className="form-control-feedback rioglyph rioglyph-error-sign"></span> : null}
                <span className="help-block">
                  <span>{errors.value}</span>
                </span>
              </div>

              <InputSelect
                disabled={isEditMode && !!branchs.find((b) => b.id === form.branch)?.inactive}
                inputChange={inputChange}
                label={t("receipt.form.label.branch")}
                name="branch"
                newOption={newOption}
                onCreateNewOption={onCreateNewOption}
                options={branchs}
                value={form.branch}
                errorMessage={errors.branch}
                onInactiveSelect={(item) => onInactiveSelect?.(item, "branch")}
                inactiveEntityLabel="filial"
              />

              <InputSelect
                disabled={isEditMode && !!segments.find((s) => s.id === form.segment)?.inactive}
                inputChange={inputChange}
                label={t("receipt.form.label.segment")}
                name="segment"
                newOption={newOption}
                onCreateNewOption={onCreateNewOption}
                options={segments}
                value={form.segment}
                errorMessage={errors.segment}
                onInactiveSelect={(item) => onInactiveSelect?.(item, "segment")}
                inactiveEntityLabel="segmento"
              />

              <InputSelect
                disabled={isEditMode && !!suppliers.find((s) => s.id === form.supplier)?.inactive}
                inputChange={inputChange}
                label={t("receipt.form.label.client")}
                newOption={newOption}
                name="supplier"
                onCreateNewOption={onCreateNewOption}
                options={suppliers}
                value={form.supplier}
                errorMessage={errors.supplier}
                onInactiveSelect={(item) => onInactiveSelect?.(item, "supplier")}
                inactiveEntityLabel="cliente"
              />

              <InputSelect
                inputChange={inputChange}
                label={t("receipt.form.label.vehicle")}
                name="vehicle"
                onCreateNewOption={onCreateNewOption}
                options={vehicles}
                value={form.vehicle}
                errorMessage={errors.vehicle}
                disabled={isVehicleLockedByTrip}
                useClear
              />

              {driversQuantity
                ? buildDriverSlots(driversQuantity).map(({ index, slotKey }) => {
                    const label = getDriverLabel(index);
                    const isLastDriver = index === MAX_DRIVERS - 1;

                    return (
                      <InputSelect
                        key={slotKey}
                        inputChange={(_, value) =>
                          value === undefined ? inputClearDriver(index) : inputDriverChange(value, index)
                        }
                        label={label}
                        name="driver"
                        onCreateNewOption={onCreateNewOption}
                        options={drivers}
                        value={form.driver[index]}
                        errorMessage={errors.driver}
                        disabled={isDriverLockedByTrip}
                        onRight={
                          !isLastDriver
                            ? () => setDriversQuantity((prev) => Math.min(MAX_DRIVERS, prev + 1))
                            : undefined
                        }
                        iconRight={!isLastDriver ? "ioglyph rioglyph-plus-light" : undefined}
                        disabledRight={isDriverLockedByTrip || !form?.driver[index] || driversQuantity >= MAX_DRIVERS}
                        useClear
                        tooltip={isLastDriver ? DRIVER_LIMIT_TOOLTIP : undefined}
                      />
                    );
                  })
                : null}

              <InputSelect
                inputChange={(_, value) => handleTripChange(value as string | undefined)}
                label={t("receipt.form.label.trip")}
                name="trip"
                newOption={newOption}
                onCreateNewOption={onCreateNewOption}
                options={trips}
                value={form.trip}
                errorMessage={errors.trip}
                onFilterChange={setTripSearch}
                useClear
              />

              <div className="form-group">
                <label htmlFor="inputValue">
                  <span>{t("receipt.form.label.taxes")}</span>
                </label>
                <div className="input-group">
                  <span className="input-group-addon text-normal">%</span>
                  <input
                    maxLength={2}
                    id="inputValue"
                    type="text"
                    className="form-control"
                    value={form.taxes}
                    onChange={(e) => inputChange("taxes", e.target.value)}
                    name="taxes "
                  />
                </div>
                <span className="help-block">
                  <span>{errors.taxes}</span>
                </span>
              </div>

              <div className="form-group">
                <label htmlFor="inputObservation">{t("receipt.form.label.observation")}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  id="inputObservation"
                  value={form.observation}
                  onChange={(e) => inputChange("observation", e.target.value)}
                  name="observation"
                  placeholder="Digite aqui..."
                ></textarea>
              </div>
            </div>
          </fieldset>

          <fieldset>
            <div>
              <div className="form-group margin-top-20 display-flex flex-wrap" style={{ minWidth: 0 }}>
                <Button
                  onClick={handleButtonClick}
                  disabled={isStatusOnlyEdit}
                  iconName="rioglyph rioglyph-upload"
                  className="rounded-small margin-right-5"
                >
                  {t("receipt.form.action.attachReceipt")}
                </Button>
                {fileName && (
                  <span
                    className="btn btn-muted btn-sm rounded-small align-items-center gap-10"
                    style={{
                      display: "inline-flex",
                      maxWidth: "100%",
                      width: "fit-content",
                      overflow: "hidden",
                    }}
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("common.label.attachmentIndex", { n: 1 })}
                    </span>
                    {isEditMode && transactionId && !hasNewAttachment && onDownloadFile && (
                      <button
                        type="button"
                        tabIndex={0}
                        title={t("receipt.form.action.downloadReceipt")}
                        className="rioglyph rioglyph-download cursor-pointer text-color-primary"
                        style={{ ...RESET_BOTAO, flex: "0 0 auto" }}
                        onKeyDown={(evento) => {
                          if (evento.key === "Enter" || evento.key === " ") {
                            evento.preventDefault();
                            onDownloadFile(transactionId);
                          }
                        }}
                        onClick={() => onDownloadFile(transactionId)}
                      />
                    )}
                    {!isStatusOnlyEdit && (
                      <button
                        type="button"
                        tabIndex={0}
                        title={t("receipt.form.action.deleteReceipt")}
                        className="rioglyph rioglyph-remove-sign cursor-pointer text-color-danger"
                        style={{ ...RESET_BOTAO, flex: "0 0 auto" }}
                        onKeyDown={(evento) => {
                          if (evento.key === "Enter" || evento.key === " ") {
                            evento.preventDefault();
                            fileClear();
                          }
                        }}
                        onClick={fileClear}
                      />
                    )}
                  </span>
                )}
                <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} />
                {errorFile && <p className="text-color-danger margin-0 margin-top-5">{errorFile}</p>}
              </div>
            </div>
          </fieldset>
        </form>
      </div>
      <div className="display-flex justify-content-between align-items-center flex-wrap gap-10 border border-top-only border-width-1 border-color-lighter padding-20">
        <Button onClick={onCancel} className="width-150 rounded-small width-100pct-xs">
          {t("common.action.cancel")}
        </Button>
        <div className="display-flex gap-10 flex-wrap width-100pct-xs">
          {isEditMode ? null : (
            <Button onClick={handleSubmitNew} className="width-150 rounded-small width-100pct-xs">
              {t("receipt.form.action.saveAndCreateNew")}
            </Button>
          )}
          <Button onClick={handleSubmit} bsStyle={Button.PRIMARY} className="width-150 rounded-small width-100pct-xs">
            {t("common.action.save")}
          </Button>
        </div>
      </div>
    </>
  );
};

export default ReceiptForm;
